"use client";
import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { env } from "@/lib/environment";

import { Section } from "@/components/ui/Section";
import { Typography, Button } from "@/components/ui";

const imgBaseUrl = `${env.NEXT_PUBLIC_CDN_URL}/images/industries/retail-digital-signage-software`;

interface impactfulContentList {
  id: number;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
}

interface ImpactfulContentProps {
  title: string;
  description: string;
  impactfulContentList: impactfulContentList[];
}
export default function ImpactfulContent({
  title,
  description,
  impactfulContentList,
}: ImpactfulContentProps) {
  const [activeContent, setActiveContent] = useState(0);


  useEffect(() => {
    const interval = setInterval(() => {
      setActiveContent(prev => (prev + 1) % impactfulContentList.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [impactfulContentList]);

  if(impactfulContentList.length === 0) return null;


  return (
    <Section variant="default" background="transparent" className="bg-secondary/5">
      <div className="text-center mb-16 max-w-4xl mx-auto">
        <Typography variant="h2" as="h2" className="mb-4">
          {title}
        </Typography>
        <Typography variant="body" as="p">
          {description}
        </Typography>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 xl:gap-12 items-center">
        <div className="space-y-8">
          <div className="space-y-4">
            {impactfulContentList.map((content, index) => (
              <button
                key={index}
                className="w-full text-left border-b border-gray-200 pb-7 pt-3 transition-all duration-300 ease-in-out"
                onClick={() => setActiveContent(content.id)}
                aria-expanded={activeContent === content.id}
                aria-controls={`content-${content.id}`}
                type="button"
              >
                <Typography variant="h4" as="h3">
                  {content.title}
                </Typography>
                <div
                  id={`content-${content.id}`}
                  className="overflow-hidden transition-all duration-300 ease-in-out"
                  style={{ height: activeContent === content.id ? "auto" : "0" }}
                  aria-hidden={activeContent !== content.id}
                >
                  <Typography variant="body" as="p" className="mt-2">
                    {content.description}
                  </Typography>
                </div>
              </button>
            ))}
          </div>

          <div className="flex flex-col items-center justify-center lg:justify-start sm:flex-row gap-4">

              <Link
                href="/company/contact"
                data-mixpanel-id="other-book-consultation"
              >
                 <Button variant="primary" size="md" asChild>
                  Book a Free Consultation
                </Button>
              </Link>


            <Link
              href="https://www.pickcel.com/digital-signage-integrations"
              target="_blank"
              rel="noopener noreferrer"
              data-mixpanel-id="other-discover-integrations"
            >
              <Button variant="outlinePrimary" size="md" asChild>
                Discover all our Integrations
              </Button>
            </Link>
          </div>
        </div>

        <div className="relative">
          <Image
            src={impactfulContentList[activeContent].image}
            alt={impactfulContentList[activeContent].imageAlt}
            title={impactfulContentList[activeContent].imageAlt}
            width={600}
            height={400}
            loading="lazy"
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 600px"
            className="w-full h-auto "
          />
        </div>
      </div>
    </Section>
  );
}
